import type {
  DeepPartial,
  FieldErrors,
  UseFormGetValues,
  UseFormRegister,
  UseFormReset,
  UseFormSetValue,
} from "react-hook-form";
import Listener from "./listener";

import type { ValidationType } from "./validations";
import React from "react";

export type ComponentType = "input" | "select" | "custom" | "render";
export type LabelPosition = "left" | "right";

export type InputField = Input | Select | CustomControl | RenderField;
export type FormElement = InputField | Layout<FormElement>;

export type BaseFormElement<T extends ComponentType> = {
  id?: string;
  name: string;
  readonly type: T;
  value?: boolean | Date | number | string | string[];
  validations?: ValidationType[];
  validation?: {
    required?: boolean;
    minLength?: number;
    maxLength?: number;
    pattern?: string;
    min?: number;
    max?: number;
    step?: number;
  };
  uiOptions?: {
    inputType?:
      | "button"
      | "checkbox"
      | "color"
      | "date"
      | "datetime-local"
      | "email"
      | "file"
      | "hidden"
      | "image"
      | "month"
      | "number"
      | "password"
      | "radio"
      | "range"
      | "reset"
      | "search"
      | "submit"
      | "tel"
      | "text"
      | "time"
      | "url"
      | "week";

    attributes?: {
      step?: string;
    };
    placeholder?: string;
    label?: string;
    conditionalLabel?: (currentFormFields: Record<string, any>) => string;
    labelTooltip?: string;
    disabled?: boolean;
    className?: string;
    readOnly?: boolean;
    when?: boolean;
    visible?: (currentFormFields: Record<string, any>) => boolean;
    labelClassName?: string;
    labelPosition?: LabelPosition;
    isSearchable?: boolean;
    testId?: string;
  };
};

export interface RenderField extends BaseFormElement<"render"> {
  render: (field: Field, formProps: FormProps) => any;
}
export interface Input extends BaseFormElement<"input"> {
  componentOptions?: {
    debounce?: number;
    autoComplete?: boolean;
    size?: number;
  };
  value?: string;
}

export interface Select extends BaseFormElement<"select"> {
  value?: string | number;
  options: { label: string; value: string; data: any; disabled?: boolean }[];
  componentOptions?: {
    showEmpty?: number;
  };
  listen?: {
    on: string;
    execute: (setter: any) => (newValue: string | number) => void; // later
  };
  hideSeparator?: boolean;
  menuPlacement?: "top" | "bottom";
}

export interface CustomControl extends BaseFormElement<"custom"> {
  readonly customControlType: string;
}

export interface Layout<T> {
  render?: (layoutProps: LayoutProps) => React.ReactNode | JSX.Element;
  children?: T[];
  className?: string;
  uiOptions?: {
    noElement?: boolean;
  };
}

export type FormProps = {
  register: UseFormRegister<any>; // typescript won won the battle but the war is not over ⚔️
  control: any; // typescript won won the battle but the war is not over ⚔️
  errors: FieldErrors<any>; // typescript won won the battle but the war is not over ⚔️
  listeners: Listener;
  error?: string;
  formValues: Record<string, any>;
  setValue: UseFormSetValue<any>;
  getValues: UseFormGetValues<any>;
};

export type LayoutProps = {
  errors: FieldErrors<any>; // typescript won won the battle but the war is not over ⚔️
  formValues: Record<string, any>;
  setValue: UseFormSetValue<any>;
  reset: UseFormReset<any>;
};

export type Field = InputField | Layout<Field>;

export type Values<T> = DeepPartial<T>;
export type FieldChange<T> = (
  field: string,
  change: { old: any; new: any },
  values: T
) => void;

export const isLayout = <T>(obj: any): obj is Layout<T> =>
  obj?.type === undefined;
